var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"V-jHHDCrIPyKXAv1p6s2_"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "/static/nextjs";

// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";
import { isCancel } from "axios";

// eslint-disable-next-line prefer-destructuring
const ENABLE_SENTRY = process.env.ENABLE_SENTRY;
const SENTRY_DSN = process.env.SENTRY_DSN;
const APP_ENV = process.env.APP_ENV;

/**
 * Third-party tracking/marketing domains that commonly cause errors due to:
 * - Ad blockers blocking requests
 * - Network issues with external services
 * - Problems with the third-party services themselves
 *
 * These errors are not actionable and create noise in Sentry.
 */
const THIRD_PARTY_TRACKING_DOMAINS = [
  // Google advertising/analytics (often blocked by ad blockers)
  /googlesyndication\.com/i,
  /doubleclick\.net/i,
  /google-analytics\.com/i,
  /googleadservices\.com/i,
  // Marketing and tracking scripts
  /optimonk\.com/i,
  /quantcount\.com/i,
  /braze\.com/i,
  /reddit\.com/i,
];

/**
 * Check if any regex in the list matches the given string
 */
const matchesThirdPartyDomain = (str) =>
  THIRD_PARTY_TRACKING_DOMAINS.some((regex) => regex.test(str));

// conditionally call Sentry.init to disable sentry
if (ENABLE_SENTRY === "on") {
  Sentry.init({
    environment: APP_ENV,
    dsn: SENTRY_DSN,
    sampleRate: 1.0, // Sampling Error Events

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: 0.0, // Sampling Transaction Events - Uniform Sample Rate
    replaysOnErrorSampleRate: 0.05, // send session replay for errors
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const modifiedEvent = event;

      // Filter out axios cancel errors
      if (isCancel(error)) {
        return null;
      }

      // Filter out the specific in-app browser TypeError and similar variations
      if (
        error instanceof Error &&
        error.message &&
        error.message.includes(
          "undefined is not an object (evaluating 'window.webkit.messageHandlers"
        )
      ) {
        return null;
      }

      // If the error is an axios error, we want to send the actual error message.
      // @ts-expect-error Error is unknown
      if (error && error.isAxiosError && error.response && event.exception) {
        // Send only 500 server errors if error is an axios error
        // @ts-expect-error Error is unknown
        if (Math.floor(error.response.status / 100) === 5) {
          // @ts-expect-error Error is unknown
          modifiedEvent.exception.values[0].value = error.response.data?.detail;
          return modifiedEvent;
        }

        return null;
      }

      // Filter JSON parsing errors from third-party tracking scripts.
      // These scripts (OptimOnk, QuantCount, Braze, Reddit Pixel, etc.) are not under our control.
      if (
        error &&
        error instanceof Error &&
        (error.message.includes("Unexpected end of JSON input") ||
          error.message.includes("JSON.parse: unexpected end of data"))
      ) {
        // Check stack trace for third-party domains
        if (typeof error.stack === "string" && matchesThirdPartyDomain(error.stack)) {
          return null;
        }

        // JSON parsing errors may not include third-party URLs in the stack trace,
        // so we also check recent network requests (breadcrumbs) to identify the source.
        if (event.breadcrumbs) {
          const hasThirdPartyRequest = event.breadcrumbs.some((breadcrumb) => {
            if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") {
              const url = breadcrumb.data?.url || "";
              return matchesThirdPartyDomain(url);
            }
            return false;
          });

          if (hasThirdPartyRequest) {
            return null;
          }
        }
      }

      // If the error is a network error from outside of Axios (ie. an error from fetch)
      // check that it is not from a third party we can expect errors from.
      if (
        error &&
        error instanceof Error &&
        error.message.includes("Failed to fetch") &&
        typeof error.stack === "string"
      ) {
        if (matchesThirdPartyDomain(error.stack)) {
          return null;
        }
      }

      return event;
    },
    integrations: [
      new Sentry.Replay({
        maskAllText: false,
      }),
    ],
    /**
     * Error filtering as recommended by Sentry.
     * @see https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/filtering/
     */
    ignoreErrors: [
      /* Recommended errors */
      // Random plugins/extensions
      "top.GLOBALS",
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "http://tt.epicplay.com",
      "Can't find variable: ZiteReader",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      "http://loading.retry.widdit.com/",
      "atomicFindClose",
      // Facebook borked
      "fb_xd_fragment",
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      "conduitPage",
      /* END Recommended errors */
      // Google flakiness
      "viewthroughconversion",
      // Third-party tracking script errors
      "Error fetching rules.js",
      "QuantJS",
    ],
    denyUrls: [
      /* Recommended errors */
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      /connect\.facebook\.net\/en_US\/iab\.autofill\.enhanced\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      /* END Recommended errors */
      // Google flakiness
      /googlesyndication\.com/i,
      /doubleclick\.net/i,
      /google-analytics\.com/i,
      /googleadservices\.com/i,
      // Marketing and tracking scripts (OptimOnk, QuantCount, Braze, Reddit Pixel)
      /optimonk\.com/i,
      /quantcount\.com/i,
      /braze\.com/i,
      /pixel-config\.reddit\.com/i,
      /pixel\.quantcount\.com/i,
    ],
  });

  // Set desktop-specific Sentry tags when running inside Electron browser shell.
  // This enables correlation of errors between showpass-electron and showpass-nextjs Sentry projects.
  if (typeof window !== "undefined" && window.ElectronView?.isElectron) {
    // Platform is available immediately
    Sentry.setTag("desktop_platform", window.ElectronView.platform);

    // desktopVersion and desktopSessionId are sent via IPC after page load, so use callback
    if (typeof window.ElectronView.onDesktopInfoReady === "function") {
      window.ElectronView.onDesktopInfoReady((info) => {
        Sentry.setTag("desktop_version", info.desktopVersion);
        Sentry.setTag("desktop_session_id", info.desktopSessionId);
      });
    }
  }
}
